import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isLyrraOne, formateurFlowsListPath, formateurSupportsListPath } from '@/lib/platform';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, FileText, Workflow, Podcast,
  ArrowRight, Clock, CornerDownLeft, Loader2,
  Megaphone, Users, Package, MessageCircle, Video,
  Calendar, Image as ImageIcon, Film, Music, Zap, Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FieldShellLeadSlot,
  fieldShellInnerInputClass,
  fieldShellWrapperClass,
} from '@/components/ui/field-shell';
import { api } from '@/lib/api';
import { useUIStore } from '@/stores/ui.store';
import { buildEduFlowEditUrl } from '@/lib/eduflow-designer-return';
import { LYRRAONE_HUB_MODULES } from '@/lib/lyrraone-hub';

type SearchCategory =
  | 'page'
  | 'formation'
  | 'eduflow'
  | 'podcast'
  | 'funnel'
  | 'contact'
  | 'bundle'
  | 'community'
  | 'webinar'
  | 'booking'
  | 'physical'
  | 'image'
  | 'document'
  | 'video'
  | 'sound'
  | 'automation'
  | 'email';

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  category: SearchCategory;
  path: string;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  page: FileText,
  formation: Workflow,
  eduflow: Workflow,
  podcast: Podcast,
  funnel: Megaphone,
  contact: Users,
  bundle: Package,
  community: MessageCircle,
  webinar: Video,
  booking: Calendar,
  physical: Package,
  image: ImageIcon,
  document: FileText,
  video: Film,
  sound: Music,
  automation: Zap,
  email: Mail,
};

const STATIC_PAGES: SearchResult[] = [
  { id: 'p-dashboard', title: 'Dashboard', category: 'page', path: '/dashboard' },
  { id: 'p-eduflow', title: 'Formations', category: 'page', path: '/eduflow/list' },
  { id: 'p-podcasts', title: 'Podcasts', category: 'page', path: '/podcasts' },
  { id: 'p-learners', title: 'Learners', category: 'page', path: '/learners' },
  { id: 'p-analytics', title: 'Analytics', category: 'page', path: '/analytics' },
  { id: 'p-videos', title: 'Videos', category: 'page', path: '/videos' },
  { id: 'p-sounds', title: 'Sounds', category: 'page', path: '/sounds' },
  { id: 'p-images', title: 'Images', category: 'page', path: '/images' },
  { id: 'p-documents', title: 'Documents', category: 'page', path: '/documents' },
  { id: 'p-help', title: 'Help', category: 'page', path: '/help' },
];

function matchesQuery(q: string, ...fields: (string | undefined)[]) {
  const lower = q.toLowerCase();
  return fields.some((f) => f?.toLowerCase().includes(lower));
}

function getLyrraOneStaticPages(): SearchResult[] {
  const hubPages: SearchResult[] = LYRRAONE_HUB_MODULES.filter((m) => m.key !== 'hub').map((m) => ({
    id: `lo-${m.key}`,
    title: m.label,
    description: m.desc || undefined,
    category: 'page' as const,
    path: m.path,
  }));

  const extra: SearchResult[] = [
    { id: 'p-dashboard', title: 'Dashboard', category: 'page', path: '/dashboard' },
    {
      id: 'p-flows',
      title: 'Formations',
      category: 'page',
      path: formateurFlowsListPath(),
      description: 'Parcours pédagogiques',
    },
    {
      id: 'p-podcasts',
      title: 'Podcasts',
      category: 'page',
      path: formateurSupportsListPath(),
    },
    { id: 'p-images', title: 'Images', category: 'page', path: '/images' },
    { id: 'p-documents', title: 'Documents', category: 'page', path: '/documents' },
    { id: 'p-videos', title: 'Vidéos', category: 'page', path: '/videos' },
    { id: 'p-sounds', title: 'Sons', category: 'page', path: '/sounds' },
    { id: 'p-settings', title: 'Paramètres', category: 'page', path: '/settings' },
    { id: 'p-analytics', title: 'Analytics', category: 'page', path: '/analytics' },
    { id: 'p-help', title: 'Aide', category: 'page', path: '/help' },
  ];

  return [...hubPages, ...extra];
}

function getStaticPages(): SearchResult[] {
  const flowsPath = formateurFlowsListPath();
  const supportsPath = formateurSupportsListPath();
  return STATIC_PAGES.map((page) =>
    page.id === 'p-learners' && isLyrraOne()
      ? { id: 'p-contacts', title: 'Contacts', category: 'page' as const, path: '/marketing/contacts' }
      : page.id === 'p-eduflow'
        ? { ...page, path: flowsPath }
        : page.id === 'p-podcasts'
          ? { ...page, path: supportsPath }
          : page,
  );
}

function normalizeApiHit(hit: SearchResult): SearchResult {
  let path = hit.path;

  if (hit.category === 'formation' || hit.category === 'eduflow') {
    const flowId = path.match(/\/eduflow\/([^/?]+)/)?.[1];
    if (flowId) path = buildEduFlowEditUrl(flowId, '/dashboard');
  }

  if (hit.category === 'podcast') {
    const podcastId = path.match(/edit=([^&]+)/)?.[1];
    if (podcastId) path = formateurSupportsListPath({ edit: podcastId });
  }

  return { ...hit, path };
}

const RECENT_SEARCHES_KEY = 'lyrra_recent_searches';

function getRecentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecentSearch(query: string) {
  const recent = getRecentSearches().filter((s) => s !== query);
  recent.unshift(query);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, 5)));
}

function unwrapList(resp: unknown): any[] {
  if (!resp || typeof resp !== 'object') return [];
  const r = resp as Record<string, unknown>;
  if (Array.isArray(r.data)) return r.data;
  if (Array.isArray(r)) return r;
  if (r.data && typeof r.data === 'object' && Array.isArray((r.data as any).data)) return (r.data as any).data;
  return [];
}

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleCommandPalette } = useUIStore();

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches] = useState<string[]>(getRecentSearches);
  const [apiResults, setApiResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const pageResults = query.trim()
    ? (isLyrraOne() ? getLyrraOneStaticPages() : getStaticPages()).filter((r) =>
        matchesQuery(query.trim(), r.title, r.description, r.path),
      )
    : [];

  const allResults = [...pageResults, ...apiResults];

  const groupedResults = allResults.reduce<Record<string, SearchResult[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, apiResults]);

  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2) {
      setApiResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const timer = setTimeout(async () => {
      try {
        if (isLyrraOne()) {
          const resp = await api.get<unknown>('/lyrraone/search', { q });
          if (controller.signal.aborted) return;

          const results: SearchResult[] = unwrapList(resp)
            .filter((item) => item?.id && item?.title)
            .map((item) => normalizeApiHit({
              id: String(item.id),
              title: String(item.title),
              description: item.description ? String(item.description) : undefined,
              category: (item.category || 'page') as SearchCategory,
              path: String(item.path || '/dashboard'),
            }));

          setApiResults(results);
          return;
        }

        const [eduflowsResp, podcastsResp] = await Promise.allSettled([
          api.get<unknown>('/eduflows', { search: q, limit: 8 }),
          api.get<unknown>('/podcasts', { search: q, limit: 8 }),
        ]);

        if (controller.signal.aborted) return;

        const results: SearchResult[] = [];

        if (eduflowsResp.status === 'fulfilled') {
          for (const item of unwrapList(eduflowsResp.value)) {
            if (!item?.id) continue;
            const title = item.title || '';
            const desc = item.description || '';
            if (matchesQuery(q, title, desc)) {
              results.push(normalizeApiHit({
                id: `ef-${item.id}`,
                title,
                description: desc ? desc.slice(0, 80) : undefined,
                category: 'eduflow',
                path: buildEduFlowEditUrl(item.id, '/dashboard'),
              }));
            }
          }
        }

        if (podcastsResp.status === 'fulfilled') {
          for (const item of unwrapList(podcastsResp.value)) {
            if (!item?.id) continue;
            const title = item.title || '';
            const desc = item.description || '';
            if (matchesQuery(q, title, desc)) {
              results.push(normalizeApiHit({
                id: `pod-${item.id}`,
                title,
                description: desc ? desc.slice(0, 80) : undefined,
                category: 'podcast',
                path: formateurSupportsListPath({ edit: String(item.id) }),
              }));
            }
          }
        }

        setApiResults(results);
      } catch {
        if (!controller.signal.aborted) setApiResults([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const handleClose = useCallback(() => {
    toggleCommandPalette();
  }, [toggleCommandPalette]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      saveRecentSearch(result.title);
      navigate(result.path);
      handleClose();
    },
    [navigate, handleClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (allResults[selectedIndex]) {
            handleSelect(allResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
      }
    },
    [allResults, selectedIndex, handleSelect, handleClose],
  );

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const categoryLabel = (cat: string) => {
    switch (cat) {
      case 'page': return t('search.pages', 'Pages');
      case 'formation':
      case 'eduflow': return t('search.formations', 'Formations');
      case 'podcast': return t('search.podcasts', 'Podcasts');
      case 'funnel': return t('search.funnels', 'Tunnels de vente');
      case 'contact': return t('search.contacts', 'Contacts');
      case 'bundle': return t('search.bundles', 'Packs formations');
      case 'community': return t('search.communities', 'Communautés');
      case 'webinar': return t('search.webinars', 'Webinaires');
      case 'booking': return t('search.booking', 'Calendrier');
      case 'physical': return t('search.physical', 'Produits physiques');
      case 'image': return t('search.images', 'Images');
      case 'document': return t('search.documents', 'Documents');
      case 'video': return t('search.videos', 'Vidéos');
      case 'sound': return t('search.sounds', 'Sons');
      case 'automation': return t('search.automations', 'Automatisations');
      case 'email': return t('search.email', 'Email marketing');
      default: return cat;
    }
  };

  let flatIndex = 0;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
          aria-hidden="true"
        />

        <motion.div
          className="relative w-full max-w-xl mx-4 rounded-xl border border-[#E8EDF5] dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.15 }}
          role="dialog"
          aria-label={t('search.title', 'Search')}
        >
          <div className="border-b border-[#F0F4FA] p-3 dark:border-gray-700">
            <div className={cn(fieldShellWrapperClass, 'min-h-[3rem] gap-2 px-2 py-1.5')}>
              <FieldShellLeadSlot>
                {loading ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary-600 dark:text-primary-300" aria-hidden />
                ) : (
                  <Search className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300" aria-hidden />
                )}
              </FieldShellLeadSlot>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('search.placeholder', 'Rechercher formations, tunnels, contacts...')}
                className={fieldShellInnerInputClass()}
                aria-label={t('search.input', 'Search input')}
                autoComplete="off"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="shrink-0 rounded-md p-1 text-[#5B6472] hover:text-[#0C1C38] dark:text-[#B6CAEC] dark:hover:text-gray-200"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>

          <div ref={listRef} className="max-h-80 overflow-y-auto">
            {!query.trim() && recentSearches.length > 0 && (
              <div className="px-3 py-2">
                <p className="px-2 py-1.5 text-xs font-semibold text-[#5B6472]/70 dark:text-[#B6CAEC] uppercase tracking-wider">
                  {t('search.recent', 'Recent')}
                </p>
                {recentSearches.map((search, i) => (
                  <button
                    key={i}
                    onClick={() => setQuery(search)}
                    className="flex w-full items-center gap-3 px-2 py-2 rounded-lg text-sm text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F8FAFC] dark:hover:bg-gray-700 transition-colors"
                  >
                    <Clock className="h-4 w-4 text-[#5B6472]/70" />
                    <span>{search}</span>
                  </button>
                ))}
              </div>
            )}

            {query.trim() && !loading && allResults.length === 0 && (
              <div className="flex flex-col items-center py-10 text-center">
                <Search className="h-8 w-8 text-[#5B6472]/50 dark:text-[#B6CAEC] mb-2" />
                <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('search.noResults', 'No results for "{{query}}"', { query })}
                </p>
              </div>
            )}

            {Object.entries(groupedResults).map(([category, results]) => {
              const CategoryIcon = CATEGORY_ICONS[category] ?? FileText;
              return (
                <div key={category} className="px-3 py-2">
                  <p className="px-2 py-1.5 text-xs font-semibold text-[#5B6472]/70 dark:text-[#B6CAEC] uppercase tracking-wider">
                    {categoryLabel(category)}
                  </p>
                  {results.map((result) => {
                    const idx = flatIndex++;
                    return (
                      <button
                        key={result.id}
                        data-index={idx}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          'flex w-full items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-colors',
                          idx === selectedIndex
                            ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)]'
                            : 'text-[#121826] dark:text-[#DCE7F7] hover:bg-[#F8FAFC] dark:hover:bg-gray-700',
                        )}
                      >
                        <CategoryIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0 text-start">
                          <span className="block truncate">{result.title}</span>
                          {result.description && (
                            <span className="block truncate text-xs text-[#5B6472]/70 dark:text-[#B6CAEC]">
                              {result.description}
                            </span>
                          )}
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 opacity-50 rtl:rotate-180" />
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-4 px-4 py-2.5 border-t border-[#F0F4FA] dark:border-gray-700 text-[11px] text-[#5B6472]/70 dark:text-[#B6CAEC]">
            <span className="flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" /> {t('search.select', 'to select')}
            </span>
            <span className="flex items-center gap-1">
              ↑↓ {t('search.navigate', 'to navigate')}
            </span>
            <span className="flex items-center gap-1">
              esc {t('search.close', 'to close')}
            </span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default GlobalSearch;
